import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import FullStory from '../lib/utils/full_story';
import Loading from './shared/Loading';
import ckLogo from '../lib/images/ck-nav-logo-2022.svg';
import useSessionEntity from '../hooks/useSessionEntity';
import { emitEvent, setHeight, updateParent } from '../lib/utils/modal';
import { init, loading, setPublicKey, setIsModal } from '../actions/sessions';
import { platform, subDomains } from '../lib/constants/SiteVariables';
import { useHistory, Route, Switch } from 'react-router-dom';
import { workflows, fromLearnMore } from '../App';
import classNames from 'classnames';

const coBrandedLogo = (merchant) => {
  return `https://creditkey-assets.s3-us-west-2.amazonaws.com/co_brands/${merchant.public_key}-ck-logo.svg`;
};

export default function Session(props) {
  const history = useHistory();
  const dispatch = useDispatch();
  const isLoading = useSelector((state) => state.sessions.get('loading'));
  const session = useSelector((state) => state.sessions.get('session'));
  const cart = useSessionEntity('shopping_cart');
  const company = useSessionEntity('company');
  const user = useSessionEntity('user');
  const merchant = useSessionEntity('merchant');
  const [loaded, setLoaded] = useState(false);
  const [curPath, setCurPath] = useState();
  const [logo, setLogo] = useState(ckLogo);
  const { pathname } = useLocation();

  const getRedirectPath = (session) => {
    let redirect = { pathname: '/' + session.workflow + '/' + session.action, state: '' };
    const isNewUserApplyNow = session.session_type === 'apply_now' && (['apply_now', 'new_user'].includes(session.action));

    if (
      isNewUserApplyNow &&
      document.referrer.replace(/\/$/, '') !== process.env.REACT_APP_PI4 &&
      !fromLearnMore
    ) {
      redirect.pathname = '/init/apply_now';
      redirect.state = { redirect_url: '/init/new_user' };
    }

    if (isNewUserApplyNow && subDomains.subDomain() === 'netx') redirect.pathname = '/init/netx';

    return redirect;
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const path = pathname.split('/')[2];
    setCurPath(path);

    if (path && (path !== curPath || (path && !curPath))) {
      const fs = new FullStory(path.replace(/_/g, ''));

      if (user && merchant) {
        fs.identify(user.email, {
          displayName: user.full_name,
          merchant: merchant.name,
          company_name: user.company_name,
        });
      }

      if (merchant) {
        fs.event({
          company,
          merchant,
          session,
          user,
          shopping_cart: cart,
        });
      }

      if (session && session.mode === 'modal') {
        emitEvent(user, path);
      }
    }
  }, [pathname]);

  useEffect(() => {
    const content = document.getElementById('content');

    // add height of header + footer + css spacing
    content &&
      !isLoading &&
      (!session.mode || session.mode === 'modal') &&
      setHeight(content.clientHeight + 320);
  }, [isLoading, pathname]);

  useEffect(() => {
    dispatch(setPublicKey(props.match.params));
  }, [props.match]);

  useEffect(() => {
    if (merchant && logo !== coBrandedLogo(merchant)) {
      new Promise((resolve, reject) =>
        fetch(coBrandedLogo(merchant)).then((response) =>
          response.ok
            ? resolve(setLogo(coBrandedLogo(merchant)))
            : reject('No cobranded logo found'),
        ),
      ).catch((err) => setLogo(ckLogo));
    }
  }, [merchant]);

  useEffect(() => {
    let redirectTo = '/init/existing_user';

    dispatch(init())
      .then(() => setLoaded(true))
      .catch((err) => {
        setLoaded(true);

        if (subDomains.authorized()) {
          const [merchant_key, prequal_id, params] = subDomains.parseParams(
            props.match.params,
          );

          if (params.get('modal')) dispatch(setIsModal(!!params.get('modal')));

          // update the public_key again to make sure we pick up any change
          dispatch(setPublicKey({ public_key: merchant_key }));
          redirectTo = `/apply/${merchant_key}/${prequal_id}`;
        }

        if (merchant && merchant.isOnHold) redirectTo = '/error';

        history.replace(redirectTo);
        dispatch(loading(false));
      });
  }, []);

  useEffect(() => {
    if (merchant && merchant.isOnHold) return history.push('/error');

    if (session.workflow) {
      history.push(getRedirectPath(session))
    }

    if (session.disposition === 'completed' && company) {
      history.push(`/finish/${company.underwriting_status}`);
    }
  }, [session.workflow, session.action]);

  useEffect(() => {
    if (platform() !== 'development') {
      window.ckLoadZendeskWidget();
    }
  }, [session]);

  const dismiss = () => {
    if (!session.mode || session.mode === 'modal') {
      // detect if iframed
      if (window.self !== window.top) {
        return updateParent('cancel', null);
      } else {
        window.history.back();
        return true;
      }
    }

    if (session.mode !== 'modal') {
      if (cart && session.session_type === 'standalone')
        window.location = merchant.url;

      (cart && cart.cancel_url) ? window.location = cart.cancel_url : window.location = merchant.url;
    }
  };

  return (
    <>
      <nav className="p-4 shadow-none flex justify-between rounded-none border-solid border-b-[3px] border-border-gray">
        {merchant &&
          merchant.integration_configuration.get('show_close_navigation') && (
            <>
              {!isLoading && merchant && (
                <a
                  id="cancel"
                  onClick={dismiss}
                  className="text-base cursor-pointer text-primary-blue hover:text-navy-blue"
                >
                  <FontAwesomeIcon icon="times" />
                </a>
              )}
              {isLoading && <Loading className="h-4 w-4" />}
            </>
          )}
        
        <div className={classNames('h-4 w-4', { 'hidden': merchant })} />

        <div>
          <img src={logo} />
        </div>

        <div>
          {merchant && (
            <a
              id="learn-more"
              className="text-sm"
              href={merchant.learn_more_link}
              target="_new"
            >
              <FontAwesomeIcon icon={['fal', 'question-circle']} />
            </a>
          )}
        </div>
      </nav>
      <div className="min-h-[200px] w-[470px] mx-auto container font-avenir-book">
        <div className="mx-0">
          {!loaded && <Loading className="my-6 h-14 w-14" />}

          {loaded && (
            <div role="session" className="my-6">
              <Switch>
                {workflows.map((w) => (
                  <Route key={w.path} path={w.path} component={w.component} />
                ))}
              </Switch>
            </div>
          )}
        </div>
      </div>
      <footer className="font-avenir-book text-sm text-center mx-6 px-5 my-3">
        <div className="text-light-gray">
          <span>
            Credit Key loans are issued by First Electronic Bank, Member FDIC
          </span>
          <br />
          <a
            href="https://www.creditkey.com/legal/privacy-policy"
            target="_new"
            className="mx-1"
          >
            Privacy Policy
          </a>
          |
          <a
            href="https://www.creditkey.com/legal/terms-of-service-and-electronic-disclosures"
            target="_new"
            className="mx-1"
          >
            Terms of Service
          </a>
          |
          <a
            href="https://www.firstelectronic.com/privacy-policy/"
            target="_new"
            className="mx-1"
          >
            FEB Privacy Policy
          </a>
        </div>
      </footer>
    </>
  );
}
